import React, {
    ForwardedRef,
    ChangeEvent,
    KeyboardEvent,
    MouseEvent,
    forwardRef
} from 'react';
import styled, { css } from 'styled-components';
import { lighten, darken } from 'polished';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';

import theme from '@styles/theme';
import { Description, RegularFont, FlexBox } from './StyledComponents';

type StyledInputProps = {
    NUMBER?: boolean;
    MAX_LENGTH?: boolean;
    WITH_BUTTON?: boolean;
    HIDE_COUNT?: boolean;
    REQUIRED?: boolean;
    PLACEHOLDER?: boolean;
    id?: string;
    width?: number;
    height?: number;
    margin?: string;
    border?: string;
    borderRadius?: number;
    title?: string;
    subTitle?: string;
    description?: string;
    name?: string;
    value: string;
    type?: string;
    onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onWheel?: (e: MouseEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    onKeyUp?: (e: KeyboardEvent<HTMLInputElement>) => void;
    onNumberChange?: (name: string, count: number) => void;
    error?: boolean;
    maxLength?: number;
    disabled?: boolean;
    placeholder?: string;
    subTitleColor?: string;
    fontColor?: string;
    buttonName?: string;
    buttonTitle?: string;
    buttonBgColor?: string;
    buttonFontColor?: string;
    buttonDisabled?: boolean;
};

const StyledInput = forwardRef(
    (
        {
            NUMBER,
            MAX_LENGTH,
            WITH_BUTTON,
            REQUIRED,
            PLACEHOLDER,
            HIDE_COUNT,
            id,
            width,
            height = 50,
            margin,
            border,
            borderRadius = 4,
            title,
            subTitle,
            description,
            name = '',
            value = '',
            type = 'text',
            onClick = () => null,
            onChange = () => null,
            onWheel = () => null,
            onKeyUp = () => null,
            onKeyDown = () => null,
            onNumberChange = () => null,
            error,
            maxLength,
            disabled = false,
            placeholder = '',
            subTitleColor,
            fontColor,
            buttonTitle,
            buttonName,
            buttonBgColor,
            buttonFontColor,
            buttonDisabled
        }: StyledInputProps,
        ref: ForwardedRef<HTMLInputElement>
    ) => {
        return (
            <Wrapper $width={width} $margin={margin}>
                {title && (
                    <FlexBox
                        $justifyContent="space-between"
                        $alignItems="center"
                    >
                        <ColumnTitle>
                            {title}
                            {REQUIRED && <Asterisk> *</Asterisk>}
                            {subTitle && (
                                <SubTitle $fontColor={subTitleColor}>
                                    {subTitle}
                                </SubTitle>
                            )}
                        </ColumnTitle>
                        {MAX_LENGTH && (
                            <CountBox
                                $hide={HIDE_COUNT}
                            >{`(${value.length} / ${maxLength})`}</CountBox>
                        )}
                    </FlexBox>
                )}
                <Input
                    id={id}
                    $placeholder={PLACEHOLDER}
                    $withButton={WITH_BUTTON}
                    $height={height}
                    $border={border}
                    $borderRadius={borderRadius}
                    $error={error}
                    ref={ref}
                    name={name}
                    value={value}
                    type={type}
                    onChange={onChange}
                    onWheel={onWheel}
                    onKeyUp={onKeyUp}
                    onKeyDown={onKeyDown}
                    maxLength={maxLength}
                    disabled={disabled}
                    placeholder={placeholder}
                />
                {WITH_BUTTON && (
                    <Button
                        name={buttonName}
                        onClick={onClick}
                        $bgColor={buttonBgColor}
                        $fontColor={buttonFontColor}
                        // disabled={disabled}
                        disabled={buttonDisabled}
                    >
                        {buttonTitle}
                    </Button>
                )}
                {NUMBER && (
                    <ButtonArea>
                        <ButtonUpDown onClick={() => onNumberChange(name, 1)}>
                            <IoIosArrowUp
                                color={theme.colors.lightGrayFontColor}
                                size={16}
                            />
                        </ButtonUpDown>
                        <ButtonUpDown onClick={() => onNumberChange(name, -1)}>
                            <IoIosArrowDown
                                color={theme.colors.lightGrayFontColor}
                                size={16}
                            />
                        </ButtonUpDown>
                    </ButtonArea>
                )}
                {description && (
                    <DescriptionBox>
                        <Description $fontColor={fontColor}>
                            {description}
                        </Description>
                    </DescriptionBox>
                )}
            </Wrapper>
        );
    }
);

export default React.memo(StyledInput);

const Wrapper = styled.label<{
    $width?: number;
    $margin?: string;
}>`
    position: relative;
    display: block;
    /* display: flex;
    align-items: center; */
    width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
    margin: ${({ $margin }) => ($margin ? $margin : 0)};
`;

const ColumnTitle = styled(RegularFont)`
    margin-bottom: 8px;
    font-size: 14px;
`;

const Asterisk = styled.span`
    color: ${theme.colors.activeOrange};
`;

const SubTitle = styled.span<{ $fontColor?: string }>`
    margin-left: 8px;
    color: ${({ $fontColor }) =>
        $fontColor ? $fontColor : theme.colors.lightGrayFontColor};
`;

const Input = styled.input<{
    $placeholder?: boolean;
    $width?: number;
    $height?: number;
    $border?: string;
    $withButton?: boolean;
    $borderRadius?: number;
    $error?: boolean;
}>`
    padding: ${({ $withButton }) =>
        $withButton ? '14px 100px 14px 12px' : '14px 12px'};
    width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
    height: ${({ $height }) => $height}px;
    font-size: 15px;
    font-family: PretendardRegular, sans-serif;
    border: ${({ $border }) =>
        $border ? $border : `1px solid ${theme.colors.ultraLightGrayBorder}`};
    border-radius: ${({ $borderRadius }) => $borderRadius}px;

    &::placeholder {
        font-size: 14px;
        font-family: PretendardRegular, sans-serif;
        color: ${theme.colors.deepGrayFontColor};
    }

    ${({ $placeholder }) =>
        $placeholder &&
        css`
            font-size: 20px;
            font-family: PretendardMedium, sans-serif;

            &::placeholder {
                font-size: 20px;
                font-family: PretendardRegular, sans-serif;
                color: ${theme.colors.deepGrayFontColor};
            }
        `};
    ${({ $error }) =>
        $error &&
        css`
            &:focus {
                border: ${theme.colors.activePink} 1px solid;
            }
        `};
`;

const DescriptionBox = styled.div`
    margin-left: 180px;
    width: calc(100% - 180px);
`;

const CountBox = styled.span<{ $hide?: boolean }>`
    display: ${({ $hide }) => ($hide ? 'none' : 'inline-block')};
    margin-bottom: 8px;
    color: ${theme.colors.deepGrayFontColor};
    font-size: 14px;
`;

const Button = styled.button<{ $fontColor?: string; $bgColor?: string }>`
    position: absolute;
    bottom: 10px;
    right: 12px;
    display: flex;
    align-items: center;
    padding: 0 18px;
    height: 30px;
    color: ${({ $fontColor }) =>
        $fontColor ? $fontColor : theme.colors.blackColor};
    background-color: ${({ $bgColor }) =>
        $bgColor ? $bgColor : theme.colors.ultraLightGrayBorder};
    font-size: 13px;
    /* border-radius: 4px; */
    /* line-height: 22px; */

    &:hover {
        background-color: ${darken(0.05, theme.colors.ultraLightGrayBorder)};
    }
    &:disabled {
        background-color: ${darken(0.1, theme.colors.ultraLightGrayBorder)};
        cursor: auto;
    }
`;
const ButtonArea = styled.div`
    position: absolute;
    bottom: 8px;
    right: 12px;
    display: flex;
    flex-direction: column;
`;
const ButtonUpDown = styled.button`
    width: 16px;
    height: 16px;
`;
